import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

/**
 * MemberSearch
 * Componente de busca de membros com debounce e dropdown de resultados.
 * Substitui o MemberAddSearch do projeto antigo.
 * Usa o useDebounce hook do projeto para controlar as chamadas.
 */
const MemberSearch = ({
    value = "",
    onChange,
    disabled = false,
    candidates = [],
    onSelect,
    showNoResults = false,
    noResultsLabel = "Nenhum membro encontrado.",
    placeholder = "Buscar membro para adicionar...",
    variant = "default" // "default" ou "topbar"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    // Fecha dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Abre dropdown quando há candidatos ou busca ativa
    useEffect(() => {
        if (candidates.length > 0 || showNoResults) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    }, [candidates, showNoResults]);

    const handleSelect = (candidate) => {
        onSelect?.(candidate);
        setIsOpen(false);
    };

    const isTopbar = variant === "topbar";

    const inputClasses = isTopbar
        ? "form-control text-white"
        : "form-control bg-light border-light";

    const inputStyle = isTopbar
        ? {
            paddingLeft: "38px",
            paddingRight: value ? "38px" : "12px",
            fontSize: "14px",
            backgroundColor: "rgba(255, 255, 255, 0.18)", // menos transparente
            border: "none",
            borderRadius: "6px", // um pouco mais de arredondamento
            height: "44px", // maior
        }
        : {
            paddingLeft: "38px",
            paddingRight: value ? "38px" : "12px",
            fontSize: "13px"
        };
        
    const iconColor = isTopbar ? "rgba(255, 255, 255, 0.7)" : "#adb5bd";

    const dropdownClasses = isTopbar
        ? "position-absolute w-100 p-0 shadow-lg"
        : "position-absolute w-100 bg-white border shadow-sm rounded-bottom";

    const dropdownStyle = isTopbar
        ? { 
            zIndex: 1050, 
            top: "calc(100% + 5px)", // garante q aparece logo abaixo
            left: 0,
            maxHeight: "360px", 
            overflowY: "auto",
            backgroundColor: "#020308",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "8px", // igual Notificação ou parecido (mantem borda)
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.5)",
        }
        : { zIndex: 1050, top: "100%", maxHeight: "240px", overflowY: "auto" };

    return (
        <div ref={wrapperRef} className="position-relative w-100">
            <div className="position-relative">
                <i
                    className="mdi mdi-account-search position-absolute"
                    style={{
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "18px",
                        color: iconColor,
                        pointerEvents: "none"
                    }}
                />
                <input
                    type="text"
                    className={inputClasses}
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onChange?.(e.target.value)}
                    disabled={disabled}
                    onFocus={() => {
                        if (candidates.length > 0) setIsOpen(true);
                    }}
                    style={inputStyle}
                />
                {value && (
                    <i
                        className="mdi mdi-close-circle position-absolute"
                        style={{
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "18px",
                            color: iconColor,
                            cursor: "pointer"
                        }}
                        onClick={() => onChange?.("")}
                    />
                )}
            </div>

            {isOpen && (
                <div
                    className={dropdownClasses}
                    style={dropdownStyle}
                >
                    {candidates.length > 0 ? (
                        candidates.map((c) => (
                            <div
                                key={c.id}
                                className={`d-flex align-items-center gap-2 px-3 py-3 cursor-pointer ${isTopbar ? "" : "hover-bg-light"}`}
                                style={{ 
                                    cursor: "pointer", 
                                    borderBottom: isTopbar ? "1px solid rgba(255,255,255,0.05)" : "1px solid #f8f9fa" 
                                }}
                                onClick={() => handleSelect(c)}
                            >
                                <div
                                    className="rounded-circle bg-soft-primary d-flex align-items-center justify-content-center flex-shrink-0"
                                    style={{ width: 32, height: 32 }}
                                >
                                    {c.photo ? (
                                        <img
                                            src={c.photo}
                                            alt=""
                                            className="rounded-circle"
                                            style={{ width: 32, height: 32, objectFit: "cover" }}
                                        />
                                    ) : (
                                        <i className="mdi mdi-account text-primary" />
                                    )}
                                </div>
                                <div className="overflow-hidden">
                                    <p className={`mb-0 fw-medium text-truncate ${isTopbar ? "text-white" : ""}`} style={{ fontSize: "14px" }}>
                                        {c.name || c.fullName || "Membro"}
                                    </p>
                                    {c.memberNumber && (
                                        <span className={isTopbar ? "" : "text-muted"} style={{ fontSize: "12px", color: isTopbar ? "rgba(255,255,255,0.5)" : undefined }}>
                                            ID: {c.memberNumber}
                                        </span>
                                    )}
                                </div>
                                <i className="mdi mdi-magnify ms-auto" style={{ fontSize: "18px", color: isTopbar ? "rgba(255,255,255,0.3)" : "#adb5bd" }} />
                            </div>
                        ))
                    ) : showNoResults ? (
                        <div className="text-center py-4 px-3">
                            <i className="mdi mdi-account-search-outline d-block mb-1" style={{ fontSize: "28px", color: isTopbar ? "rgba(255,255,255,0.15)" : "#adb5bd" }} />
                            <p className="small mb-0" style={{ color: isTopbar ? "rgba(255,255,255,0.4)" : "#6c757d" }}>{noResultsLabel}</p>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

MemberSearch.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    candidates: PropTypes.array,
    onSelect: PropTypes.func,
    showNoResults: PropTypes.bool,
    noResultsLabel: PropTypes.string,
    placeholder: PropTypes.string,
    variant: PropTypes.oneOf(["default", "topbar"]),
};

export default MemberSearch;;
